/**
 * Helpers pour le processus de pesée (standard vs inversé).
 * Le processus doit rester stable tout au long du cycle de vie d'une pesée.
 */

export type WeighingProcessType = 'standard' | 'inverted';

type PeseeProcessFields = {
  processus?: string | null;
  poids_brut?: number | string | null;
  poids_tare?: number | string | null;
};

const hasPositiveWeight = (value: number | string | null | undefined): boolean => {
  if (value === null || value === undefined || value === '') return false;
  const parsed = parseFloat(String(value));
  return !Number.isNaN(parsed) && parsed > 0;
};

/**
 * Résout le processus d'une pesée.
 * On fait confiance au champ `processus` en base.
 * Ne pas inférer "inversé" depuis tare-sans-brut : un 1er poids standard
 * mal enregistré dans tare serait alors traité comme un parcours inversé.
 */
export function resolveWeighingProcess(weighing: PeseeProcessFields | null | undefined): WeighingProcessType {
  if (weighing?.processus === 'inverted') return 'inverted';
  return 'standard';
}

type PeseeStepFields = PeseeProcessFields & {
  statut?: string | null;
  agent_analyse_qualite?: unknown;
  analyse_qualite?: unknown;
  nb_sacs_declares?: number | null;
};

export function hasQualityAnalysis(weighing: PeseeStepFields | null | undefined): boolean {
  if (!weighing) return false;
  return Boolean(
    weighing.agent_analyse_qualite ||
    weighing.analyse_qualite
  );
}

/**
 * Statut affiché / métier d'une pesée en cours, d'après les poids.
 * Évite d'afficher "Tare enregistrée" quand seul le brut est présent.
 */
export function getEffectivePeseeStatut(weighing: PeseeStepFields | null | undefined): string {
  if (!weighing) return '';
  const hasTare = hasPositiveWeight(weighing.poids_tare);
  const hasBrut = hasPositiveWeight(weighing.poids_brut);
  const statut = weighing.statut || '';

  if (['COMPLETEE', 'ACHEVEE', 'REFOULEE', 'ANNULEE'].includes(statut)) {
    return statut;
  }

  if (hasBrut && !hasTare) {
    if (statut === 'EN_ATTENTE_TARE' || hasQualityAnalysis(weighing)) {
      return 'EN_ATTENTE_TARE';
    }
    return 'BRUT_ENREGISTRE';
  }

  if (hasTare && !hasBrut) {
    if (weighing.processus === 'inverted') {
      return 'TARE_ENREGISTREE';
    }
    if (statut === 'EN_ATTENTE_TARE' || hasQualityAnalysis(weighing)) {
      return 'EN_ATTENTE_TARE';
    }
    return 'BRUT_ENREGISTRE';
  }

  return statut;
}

const STATUS_LABELS: Record<string, string> = {
  EN_COURS: 'En cours',
  EN_ATTENTE_TARE: 'En attente tare',
  BRUT_ENREGISTRE: 'Brut enregistré',
  TARE_ENREGISTREE: 'Tare enregistrée',
  COMPLETEE: 'Complétée',
  ACHEVEE: 'Achevée',
  REFOULEE: 'Refoulée',
  ANNULEE: 'Annulée',
};

export function getPeseeStatusLabel(weighing: PeseeStepFields | null | undefined): string {
  const code = getEffectivePeseeStatut(weighing);
  const inverted = resolveWeighingProcess(weighing) === 'inverted';
  const hasBrut = hasPositiveWeight(weighing?.poids_brut);

  if (code === 'TARE_ENREGISTREE' && inverted && !hasBrut) {
    return 'En attente brut';
  }
  if (code === 'EN_ATTENTE_TARE' && inverted) {
    return 'En attente brut';
  }
  return STATUS_LABELS[code] || code || '';
}

export function getInProgressTargetStep(weighing: PeseeStepFields | null | undefined): number {
  if (!weighing) return 1;
  const hasTare = hasPositiveWeight(weighing.poids_tare);
  const hasBrut = hasPositiveWeight(weighing.poids_brut);
  const qualityDone = hasQualityAnalysis(weighing);
  const statut = weighing.statut || '';

  if ((statut === 'COMPLETEE' || statut === 'ACHEVEE') && hasBrut && hasTare) {
    return 4;
  }
  if (hasBrut && hasTare) return 4;
  if (hasBrut && !hasTare) return qualityDone ? 3 : 2;
  if (hasTare && !hasBrut) return qualityDone ? 3 : 2;
  return 1;
}

export function isInvertedWeighing(weighing: PeseeProcessFields | null | undefined): boolean {
  return resolveWeighingProcess(weighing) === 'inverted';
}

export function getWeighingProcessLabel(weighing: PeseeProcessFields | null | undefined): string {
  return resolveWeighingProcess(weighing) === 'inverted'
    ? 'Parcours inversé'
    : 'Parcours standard';
}

export function getWeighingProcessBadgeClass(weighing: PeseeProcessFields | null | undefined): string {
  return resolveWeighingProcess(weighing) === 'inverted'
    ? 'bg-violet-100 text-violet-800'
    : 'bg-sky-100 text-sky-800';
}
